import { DIFFICULTIES, setDifficulty, loadDifficulty, type DifficultyId } from '../game/difficulty';

/**
 * TITLE SCREEN — the arcade convention.
 *
 * The game announces itself before it starts. Beyond tradition this does two
 * practical jobs: it gives the browser a user gesture to unlock the AudioContext
 * (so the score is playing from the first frame of gameplay rather than fading
 * in awkwardly), and it states the fiction before the player is asked to act.
 *
 * It holds the simulation paused until dismissed, so nothing is missed while
 * the player is reading.
 */
export class TitleScreen {
  private readonly root = document.querySelector<HTMLElement>('#title-screen');
  private readonly startButton = document.querySelector<HTMLElement>('#title-start');
  private dismissed = false;
  private onStart: (() => void) | null = null;
  private readonly keyHandler = (event: KeyboardEvent) => {
    if (this.dismissed) return;
    if (event.key === 'ArrowDown' || event.key === 's') {
      event.preventDefault();
      this.highlight((this.selected + 1) % this.options.length);
      return;
    }
    if (event.key === 'ArrowUp' || event.key === 'w') {
      event.preventDefault();
      this.highlight((this.selected - 1 + this.options.length) % this.options.length);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.choose(this.selected);
    }
  };

  private readonly options = Array.from(
    document.querySelectorAll<HTMLElement>('.title-option'),
  );
  private selected = 0;
  private readonly intro = document.querySelector<HTMLElement>('#stage-intro');
  private introShown = false;

  private readonly film = document.querySelector<HTMLVideoElement>('#title-film-video');

  constructor() {
    window.addEventListener('keydown', this.keyHandler);
    this.startButton?.addEventListener('click', () => this.dismiss());

    // A viewer who asked for reduced motion gets the poster frame instead of a
    // moving plate. Pausing rather than hiding keeps the composition intact.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      this.film?.pause();
    } else {
      // Some browsers refuse the autoplay attribute but allow a muted play()
      // call. If that is also refused the poster stays up, which is fine.
      this.film?.play().catch(() => {});
    }
    // CONTINUE is disabled until there is a save to continue from.
    this.options.forEach((opt, i) => {
      if (opt.dataset.action === 'continue') opt.setAttribute('disabled', 'true');
      opt.addEventListener('click', () => this.choose(i));
      opt.addEventListener('mouseenter', () => this.highlight(i));
    });
    // Difficulty selector. Restores the previous choice so a player who picked
    // RECRUIT does not have to re-pick it every launch.
    const saved = loadDifficulty();
    const diffButtons = Array.from(document.querySelectorAll<HTMLElement>('.diff-option'));
    const blurb = document.querySelector<HTMLElement>('#difficulty-blurb');
    const applyDiff = (id: DifficultyId) => {
      setDifficulty(id);
      diffButtons.forEach((b) => b.classList.toggle('selected', b.dataset.diff === id));
      if (blurb) blurb.textContent = DIFFICULTIES[id].blurb;
    };
    diffButtons.forEach((b) => {
      b.addEventListener('click', () => applyDiff(b.dataset.diff as DifficultyId));
    });
    applyDiff(saved.id);

    this.highlight(0);
  }

  private highlight(i: number): void {
    this.selected = i;
    this.options.forEach((o, k) => o.classList.toggle('selected', k === i));
  }

  private choose(i: number): void {
    const action = this.options[i]?.dataset.action;
    if (action === 'continue') return; // disabled until saves exist
    if (action === 'controls') {
      document.querySelector('#title-controls')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    this.dismiss();
  }

  /** True while the title is up — the game loop should hold the simulation. */
  get visible(): boolean {
    return !this.dismissed;
  }

  whenStarted(fn: () => void): void {
    this.onStart = fn;
  }

  dismiss(): void {
    if (this.dismissed) return;
    this.dismissed = true;
    this.root?.classList.add('dismissed');
    this.showIntro();
    this.onStart?.();
    // Stop decoding the film once play begins. A hidden <video> keeps
    // decoding every frame otherwise, which is GPU and battery spent on
    // something nobody can see — during the one part that needs the budget.
    // Delayed past the fade-out so it does not freeze mid-dissolve.
    window.setTimeout(() => this.film?.pause(), 700);
  }

  /**
   * Stage briefing card. The storyline beat between the menu and play — it is
   * where the fiction lands for a first-time player, and it costs 4.5 seconds.
   * Dismissible, because a demo audience should never be trapped in text.
   */
  private showIntro(): void {
    if (this.introShown || !this.intro) return;
    this.introShown = true;
    this.intro.classList.remove('hidden');
    const hide = () => {
      this.intro?.classList.add('hidden');
      window.removeEventListener('keydown', skip);
    };
    const skip = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') hide();
    };
    window.addEventListener('keydown', skip);
    window.setTimeout(hide, 4500);
  }

  /** True while the briefing card is up — the stage clock should hold. */
  get introVisible(): boolean {
    return !!this.intro && !this.intro.classList.contains('hidden');
  }

  dispose(): void {
    window.removeEventListener('keydown', this.keyHandler);
  }
}
